import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import styles from "./ChannelDetail.module.scss";
import {
  Card,
  Row,
  Col,
  Table,
  Select,
  Button,
  Input,
  Typography,
  Skeleton,
  Empty,
} from "antd";
import {
  ArrowLeftOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  ShoppingCartOutlined,
  TagOutlined,
  DollarOutlined,
  ShoppingOutlined,
  UserOutlined,
  TeamOutlined,
  SearchOutlined,
  PieChartOutlined,
} from "@ant-design/icons";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title as CTitle,
  Legend,
  Tooltip as CTooltip,
  Filler,
} from "chart.js";
import {
  ChannelAnalysisDetailBreakdown,
  ChannelAnalysisDetailChart,
  ChannelAnalysisDetailCore,
  getChannelAnalysisDetailBreakdown,
  getChannelAnalysisDetailChart,
  getChannelAnalysisDetailCore,
} from "../../../api/Services";
import {
  useDebouncedValue,
  useFetchRequestGuard,
} from "../../../helpers/FilterSearchHelper";
import CategoryLevels from "../../../components/categorylevels/CategoryLevels";
import {
  isVariationAvailable,
  variationNotAvailableLabel,
} from "../../../helpers/VariationHelper";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  CTitle,
  Legend,
  CTooltip,
  Filler
);

const { Title, Text } = Typography;
const fmt = (v?: number | null) =>
  `$${(v ?? 0).toLocaleString("es-AR", { maximumFractionDigits: 0 })}`;
const fmtN = (v?: number | null) => (v ?? 0).toLocaleString("es-AR");

const Vr: React.FC<{ value?: number | null; suffix?: string }> = ({
  value,
  suffix = "%",
}) => {
  if (!isVariationAvailable(value)) return <span>{variationNotAvailableLabel()}</span>;
  const pos = value >= 0;
  return (
    <span
      className={`${styles.variation} ${
        pos ? styles.positive : styles.negative
      }`}
    >
      {pos ? <ArrowUpOutlined /> : <ArrowDownOutlined />}{" "}
      {pos ? "+" : ""}
      {value.toFixed(1)}
      {suffix}
    </span>
  );
};

const chartOpts = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: false,
    },
  },
  scales: {
    y: {
      beginAtZero: false,
      grid: { color: "rgba(17,24,39,0.06)" },
      ticks: { color: "#8a8298", font: { size: 11 } },
    },
    x: {
      grid: { display: false },
      ticks: { color: "#8a8298", font: { size: 11 } },
    },
  },
};

const RankingList: React.FC<{
  title: string;
  rows: {
    id: number | null;
    name: string;
    venta: number;
    categoryLevels?: string[] | null;
  }[];
  color?: string;
}> = ({ title, rows, color = "#8b5cf6" }) => {
  const max = Math.max(...rows.map((r) => r.venta), 1);
  return (
    <Card className={styles.rankCard}>
      <Title level={5} className={styles.rankTitle}>
        {title}
      </Title>
      <div className={styles.rankList}>
        {rows.map((row) => (
          <div className={styles.rankItem} key={`${row.id ?? row.name}`}>
            <div className={styles.rankHeader}>
              <Text>
                {row.categoryLevels?.length ? (
                  <CategoryLevels levels={row.categoryLevels} />
                ) : (
                  row.name
                )}
              </Text>
              <Text strong>{fmt(row.venta)}</Text>
            </div>
            <div className={styles.rankTrack}>
              <div
                className={styles.rankFill}
                style={{
                  width: `${Math.max((row.venta / max) * 100, 6)}%`,
                  background: color,
                }}
              />
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
};

const ChannelDetailBodySkeleton: React.FC = () => (
  <>
    <div className={styles.kpiRow}>
      {Array.from({ length: 4 }).map((_, i) => (
        <Card className={styles.kpiCard} key={i}>
          <Skeleton active paragraph={{ rows: 3, width: ["70%", "90%", "60%"] }} title={false} />
        </Card>
      ))}
    </div>
    <Card className={styles.chartCard}>
      <Skeleton.Input active size="small" style={{ width: 180, height: 20, marginBottom: 8 }} />
      <Skeleton.Input active size="small" style={{ width: 280, height: 14, marginBottom: 16 }} />
      <Skeleton.Input active style={{ width: "100%", height: 220, marginBottom: 16 }} />
      <div className={styles.chartSummaryRow}>
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton.Input active key={i} style={{ flex: 1, height: 72 }} />
        ))}
      </div>
    </Card>
    <Card className={styles.sectionCard}>
      <Skeleton active paragraph={{ rows: 10 }} />
    </Card>
    <Row gutter={[16, 16]} className={styles.rankRow}>
      {Array.from({ length: 3 }).map((_, i) => (
        <Col xs={24} lg={8} key={i}>
          <Card className={styles.rankCard}>
            <Skeleton active paragraph={{ rows: 5 }} />
          </Card>
        </Col>
      ))}
    </Row>
    <Card className={styles.clientsCard}>
      <Skeleton active paragraph={{ rows: 12 }} />
    </Card>
  </>
);

const ChannelDetail: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const channelId = Number(id);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search);
  const [period, setPeriod] = useState(30);
  const [core, setCore] = useState<ChannelAnalysisDetailCore | null>(null);
  const [chart, setChart] = useState<ChannelAnalysisDetailChart | null>(null);
  const [breakdown, setBreakdown] =
    useState<ChannelAnalysisDetailBreakdown | null>(null);
  const [coreLoading, setCoreLoading] = useState(true);
  const [chartLoading, setChartLoading] = useState(true);
  const [breakdownLoading, setBreakdownLoading] = useState(true);
  const {
    beginRequest: beginCoreRequest,
    isStaleRequest: isStaleCoreRequest,
  } = useFetchRequestGuard();
  const {
    beginRequest: beginBreakdownRequest,
    isStaleRequest: isStaleBreakdownRequest,
  } = useFetchRequestGuard();
  const skipSearchOnlyBreakdown = useRef(true);
  const searchRef = useRef(debouncedSearch);
  searchRef.current = debouncedSearch;

  const loadBreakdown = useCallback(
    (searchValue: string | undefined) => {
      if (!channelId) return;
      const requestId = beginBreakdownRequest();
      setBreakdownLoading(true);
      getChannelAnalysisDetailBreakdown(channelId, {
        period,
        search: searchValue || undefined,
      }).then((result) => {
        if (isStaleBreakdownRequest(requestId)) return;
        setBreakdown(result);
        setBreakdownLoading(false);
      });
    },
    [channelId, period, beginBreakdownRequest, isStaleBreakdownRequest]
  );
  const loadBreakdownRef = useRef(loadBreakdown);
  loadBreakdownRef.current = loadBreakdown;

  // Core first (KPIs), then chart + breakdown in parallel — matches Brand detail and
  // avoids three heavy requests competing for Hikari on first paint.
  // Intentionally omits search from deps so typing product mix does not re-hit core/chart.
  const fetchCoreThenRest = useCallback(async () => {
    if (!channelId) return;
    const requestId = beginCoreRequest();
    setCoreLoading(true);
    setChartLoading(true);
    setBreakdownLoading(true);

    const coreResult = await getChannelAnalysisDetailCore(channelId, { period });
    if (isStaleCoreRequest(requestId)) return;
    setCore(coreResult);
    setCoreLoading(false);

    getChannelAnalysisDetailChart(channelId, { period }).then((result) => {
      if (isStaleCoreRequest(requestId)) return;
      setChart(result);
      setChartLoading(false);
    });

    loadBreakdownRef.current(searchRef.current || undefined);
  }, [channelId, period, beginCoreRequest, isStaleCoreRequest]);

  useEffect(() => {
    fetchCoreThenRest();
  }, [fetchCoreThenRest]);

  // Product search only affects mix rows — do not re-hit core/chart.
  useEffect(() => {
    if (skipSearchOnlyBreakdown.current) {
      skipSearchOnlyBreakdown.current = false;
      return;
    }
    loadBreakdownRef.current(debouncedSearch || undefined);
  }, [debouncedSearch]);

  const salesEvolution = useMemo(
    () => chart?.salesEvolution || [],
    [chart?.salesEvolution]
  );

  const forecastTotal = useMemo(
    () =>
      salesEvolution.reduce(
        (sum, point) => sum + (point.salesForecast ?? 0),
        0
      ),
    [salesEvolution]
  );

  const dynamicChartData = useMemo(
    () => ({
      labels: salesEvolution.map((point) => point.label),
      datasets: [
        {
          label: t("ChannelDetail-RealSales"),
          data: salesEvolution.map((point) => point.salesObserved),
          borderColor: "#8b5cf6",
          backgroundColor: "rgba(139,92,246,0.08)",
          fill: true,
          tension: 0.35,
          pointRadius: 2,
          borderWidth: 3,
          spanGaps: true,
        },
        {
          label: t("ChannelDetail-Forecast"),
          data: salesEvolution.map((point) => point.salesForecast),
          borderColor: "rgba(139,92,246,0.35)",
          borderDash: [7, 5],
          backgroundColor: "transparent",
          tension: 0.35,
          pointRadius: 0,
          borderWidth: 3,
          spanGaps: true,
        },
      ],
    }),
    [salesEvolution, t]
  );

  const backBtn = (
    <Button
      icon={<ArrowLeftOutlined />}
      type="text"
      className={styles.backBtn}
      onClick={() => navigate("/commercial-analysis/channels")}
    >
      {t("ChannelAnalysis-Channels")}
    </Button>
  );

  if (coreLoading && !core) {
    return (
      <div className={styles.wrapper}>
        {backBtn}
        <Card className={styles.headerCard}>
          <Skeleton.Input active style={{ width: 280, height: 28 }} />
        </Card>
        <ChannelDetailBodySkeleton />
      </div>
    );
  }

  if (!core) {
    return (
      <div className={styles.wrapper}>
        {backBtn}
        <Empty description={t("ChannelDetail-NoChannelFound")} />
      </div>
    );
  }

  const h = core.header;
  const k = core.kpis;

  const mixCols = [
    { title: t("ChannelDetail-Brand"), dataIndex: "brandName", key: "brandName", width: 100 },
    {
      title: t("ChannelDetail-Category"),
      dataIndex: "categoryName",
      key: "categoryName",
      width: 220,
    },
    { title: t("ChannelDetail-Article"), dataIndex: "articleName", key: "articleName" },
    {
      title: t("ChannelDetail-Sale"),
      dataIndex: "ventaBruta",
      key: "ventaBruta",
      align: "right" as const,
      render: (_: any, r: any) => {
        return (
          <div className={styles.cellStack}>
            <Text strong>{fmt(r.ventaBruta)}</Text>
            <Vr value={r.ventaVar} />
          </div>
        );
      },
    },
    {
      title: t("ChannelDetail-Units"),
      key: "unidades",
      align: "right" as const,
      render: (_: any, r: any) => {
        return (
          <div className={styles.cellStack}>
            <Text strong>{fmtN(r.unidades)}</Text>
            <Vr value={r.unidadesVar} />
          </div>
        );
      },
    },
    {
      title: t("ChannelDetail-Tickets"),
      dataIndex: "tickets",
      key: "tickets",
      render: (v: number) => fmtN(v),
      align: "right" as const,
    },
    {
      title: t("ChannelDetail-AverageTicketShort"),
      dataIndex: "ticketPromedio",
      key: "ticketPromedio",
      render: (v: number) => fmt(v),
      align: "right" as const,
    },
    {
      title: t("ChannelDetail-Margin"),
      dataIndex: "margenPct",
      key: "margenPct",
      render: (v: number) => `${v.toFixed(1)}%`,
      align: "right" as const,
    },
    {
      title: t("ChannelDetail-ChannelSalesPercent"),
      dataIndex: "pctVentaCanal",
      key: "pctVentaCanal",
      render: (v: number) => `${v.toFixed(1)}%`,
      align: "right" as const,
    },
    {
      title: t("ChannelDetail-ProjectedSales"),
      key: "ventaProyectada",
      align: "right" as const,
      render: () => <Text type="secondary">—</Text>,
    },
  ];

  return (
    <div className={styles.wrapper}>
      {backBtn}

      <Card className={styles.headerCard}>
        <div className={styles.headerRow}>
          <div>
            <Title level={3} className={styles.pageTitle}>
              {h.name}
            </Title>
            {h.pctVenta != null && (
              <div className={styles.metaRow}>
                <div className={styles.metaItem}>
                  <PieChartOutlined className={styles.metaIcon} />
                  <span className={styles.metaLabel}>
                    {t("ChannelDetail-ChannelShare")}
                  </span>
                  <span className={styles.metaValue}>
                    {h.pctVenta.toFixed(1)}%
                  </span>
                </div>
              </div>
            )}
          </div>
          <div className={styles.headerFilters}>
            <Input
              placeholder={t("ChannelDetail-SearchProduct")}
              prefix={<SearchOutlined />}
              className={styles.filterInput}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              allowClear
            />
            <Select
              value={period}
              onChange={setPeriod}
              className={styles.filterSelect}
              options={[
                { value: 30, label: t("ChannelDetail-Last30Days") },
                { value: 60, label: t("ChannelDetail-Last60Days") },
                { value: 90, label: t("ChannelDetail-Last90Days") },
                { value: 180, label: t("ChannelDetail-Last180Days") },
              ]}
            />
          </div>
        </div>
      </Card>

      {coreLoading ? (
        <div className={styles.kpiRow}>
          {Array.from({ length: 4 }).map((_, i) => (
            <Card className={styles.kpiCard} key={i}>
              <Skeleton active paragraph={{ rows: 3, width: ["70%", "90%", "60%"] }} title={false} />
            </Card>
          ))}
        </div>
      ) : (
      <div className={styles.kpiRow}>
        {[
          {
            label: t("ChannelDetail-ChannelAverageTicket"),
            value: fmt(k.ticketPromedio),
            variation: k.ticketPromedioVar,
            compare: `${t("ChannelDetail-Global")}: ${fmt(k.ticketPromedioGlobal)}`,
            icon: <ShoppingCartOutlined />,
          },
          {
            label: t("ChannelDetail-GrossSales"),
            value: fmt(k.ventaBruta),
            variation: k.ventaVar,
            compare: `${t("ChannelDetail-Global")}: ${fmt(k.ventaGlobal)}`,
            icon: <DollarOutlined />,
          },
          {
            label: t("ChannelDetail-Units"),
            value: fmtN(k.unidades),
            variation: k.unidadesVar,
            compare: `${t("ChannelDetail-Global")}: ${fmtN(k.unidadesGlobal)}`,
            icon: <TagOutlined />,
          },
          {
            label: t("ChannelDetail-BasketSize"),
            value: (k.tamanoCesta ?? 0).toFixed(2),
            variation: k.tamanoCestaVar,
            compare: `${t("ChannelDetail-Global")}: ${(k.tamanoCestaGlobal ?? 0).toFixed(2)}`,
            icon: <ShoppingOutlined />,
          },
        ].map((kpi, i) => (
          <Card className={styles.kpiCard} key={i}>
            <div className={styles.kpiIcon}>{kpi.icon}</div>
            <Text className={styles.kpiLabel}>{kpi.label}</Text>
            <div className={styles.kpiValue}>{kpi.value}</div>
            <Vr value={kpi.variation} />
            <Text type="secondary" className={styles.kpiCompare}>
              {kpi.compare}
            </Text>
          </Card>
        ))}
      </div>
      )}

      {chartLoading ? (
        <Card className={styles.chartCard}>
          <Skeleton.Input active size="small" style={{ width: 180, height: 20, marginBottom: 8 }} />
          <Skeleton.Input active size="small" style={{ width: 280, height: 14, marginBottom: 16 }} />
          <Skeleton.Input active style={{ width: "100%", height: 220, marginBottom: 16 }} />
        </Card>
      ) : (
      <Card className={styles.chartCard}>
        <div className={styles.sectionHeader}>
          <Title level={5} className={styles.cardTitle}>{t("ChannelDetail-SalesTrend")}</Title>
          <Text type="secondary">{t("ChannelDetail-SalesTrendDescription")}</Text>
        </div>
        <div className={styles.chartContainer}>
          <Line data={dynamicChartData} options={chartOpts} />
        </div>
        <div className={styles.chartSummaryRow}>
          <div className={styles.summaryBox}>
            <Text>{t("ChannelDetail-LastPeriodSales")}</Text>
            <strong>{fmt(k.ventaBruta)}</strong>
            <span>{t("ChannelDetail-CurrentChannelResult")}</span>
          </div>
          <div className={styles.summaryBox}>
            <Text>{t("ChannelDetail-NextPeriodForecast")}</Text>
            <strong>{forecastTotal > 0 ? fmt(forecastTotal) : "—"}</strong>
            <span>{t("ChannelDetail-ForecastBase")}</span>
          </div>
          <div className={styles.summaryBox}>
            <Text>{t("ChannelDetail-Supply")}</Text>
            <strong>{breakdown?.supplySource || "—"}</strong>
          </div>
        </div>
      </Card>
      )}

      {breakdownLoading || !breakdown ? (
        <>
          <Card className={styles.sectionCard}>
            <Skeleton active paragraph={{ rows: 10 }} />
          </Card>
          <Row gutter={[16, 16]} className={styles.rankRow}>
            {Array.from({ length: 3 }).map((_, i) => (
              <Col xs={24} lg={8} key={i}>
                <Card className={styles.rankCard}>
                  <Skeleton active paragraph={{ rows: 5 }} />
                </Card>
              </Col>
            ))}
          </Row>
          <Card className={styles.clientsCard}>
            <Skeleton active paragraph={{ rows: 12 }} />
          </Card>
        </>
      ) : (
        <>
      <Card className={styles.sectionCard}>
        <Title level={4} className={styles.tableTitle}>
          {t("ChannelDetail-SalesMix")}
        </Title>
        <Table
          columns={mixCols}
          dataSource={(breakdown.productMix || []).map((row) => ({
            ...row,
            key: row.articleId,
          }))}
          pagination={{ pageSize: 10, showSizeChanger: false }}
          size="small"
          scroll={{ x: 1100 }}
          className={styles.dataTable}
        />
      </Card>

      <Row gutter={[16, 16]} className={styles.rankRow}>
        <Col xs={24} lg={8}>
          <RankingList
            title={t("ChannelDetail-TopBrands")}
            rows={breakdown.topBrands || []}
          />
        </Col>
        <Col xs={24} lg={8}>
          <RankingList
            title={t("ChannelDetail-TopCategories")}
            rows={breakdown.topCategories || []}
            color="#b99af7"
          />
        </Col>
        <Col xs={24} lg={8}>
          <RankingList
            title={t("ChannelDetail-TopStores")}
            rows={breakdown.topStores || []}
            color="#10bcd4"
          />
        </Col>
      </Row>

      <Card className={styles.clientsCard}>
        <Title level={5} className={styles.clientsTitle}>
          {t("ChannelDetail-ChannelClients")}
        </Title>
        <div className={styles.clientKpiRow}>
          {[
            {
              label: t("ChannelDetail-UniqueClients"),
              value: fmtN(breakdown.clients?.clientesUnicos),
              icon: <UserOutlined />,
            },
            {
              label: t("ChannelDetail-NewClients"),
              value: fmtN(breakdown.clients?.clientesNuevos),
              icon: <UserOutlined />,
            },
            {
              label: t("ChannelDetail-RecurrentClients"),
              value: fmtN(breakdown.clients?.clientesRecurrentes),
              icon: <TeamOutlined />,
            },
            {
              label: t("ChannelDetail-ExclusiveChannelClients"),
              value: fmtN(breakdown.clients?.clientesExclusivos),
              icon: <UserOutlined />,
            },
            {
              label: t("ChannelDetail-Frequency"),
              value: fmt(breakdown.clients?.frecuenciaCompra),
              icon: <ShoppingCartOutlined />,
            },
          ].map((clientKpi, i) => (
            <div className={styles.clientKpiItem} key={i}>
              <Text type="secondary" className={styles.clientKpiLabel}>
                {clientKpi.label}
              </Text>
              <div className={styles.clientKpiMain}>
                <div className={styles.clientKpiValue}>{clientKpi.value}</div>
              </div>
            </div>
          ))}
        </div>

        <Row gutter={[16, 16]} className={styles.clientTablesRow}>
          <Col xs={24} lg={12}>
          <Card className={styles.innerCard}>
            <Title level={5} className={styles.innerTitle}>
              {breakdown.newClientsByMonthYear
                ? t("ChannelDetail-NewClientsByMonthYear", {
                    year: breakdown.newClientsByMonthYear,
                  })
                : t("ChannelDetail-NewClientsByMonth")}
            </Title>
            <Table
              columns={[
                { title: t("ChannelDetail-Month"), dataIndex: "month", key: "month" },
                {
                  title: t("ChannelDetail-NewClients"),
                  dataIndex: "clientes",
                  key: "clientes",
                  render: (v: number) => fmtN(v),
                  align: "right" as const,
                },
              ]}
              dataSource={(breakdown.newClientsByMonth || []).map((row) => ({
                ...row,
                key: row.month,
              }))}
              pagination={false}
              size="small"
              className={styles.dataTable}
            />
          </Card>
          </Col>
          <Col xs={24} lg={12}>
          <Card className={styles.innerCard}>
            <Title level={5} className={styles.innerTitle}>
              {t("ChannelDetail-GeographicLocation")}
            </Title>
            <Table
              columns={[
                {
                  title: t("ChannelDetail-Location"),
                  dataIndex: "location",
                  key: "location",
                },
                {
                  title: t("ChannelDetail-Clients"),
                  dataIndex: "clientes",
                  key: "clientes",
                  render: (v: number) => fmtN(v),
                  align: "right" as const,
                },
                {
                  title: t("ChannelDetail-Growth"),
                  dataIndex: "crecimiento",
                  key: "crecimiento",
                  render: (v: number) => <Vr value={v} />,
                  align: "right" as const,
                },
              ]}
              dataSource={(breakdown.geoRows || []).map((row) => ({
                ...row,
                key: row.location,
              }))}
              pagination={false}
              size="small"
              className={styles.dataTable}
            />
          </Card>
          </Col>
        </Row>
      </Card>
        </>
      )}
    </div>
  );
};

export default ChannelDetail;
